import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { FullStory } from "@fullstory/browser";

import CampaignProvider from "containers/admin/clients/campaign/components/campaign-provider/campaign-provider";
import ClientProvider from "containers/admin/clients/client/components/client-provider/client-provider";
import CreativeProvider from "containers/admin/clients/touchpoint/components/touchpoint-provider/touchpoint-provider";
import TouchpointPageRedirect from "containers/admin/clients/touchpoint/touchpoint-page-redirect";
import TouchpointPage from "containers/admin/clients/touchpoint/touchpoint-page";
import ClientsRoutes from "containers/admin/clients/clients-routes";
import { AdminRoutes } from "containers/admin/admin-routes";
import NotFoundPage from "containers/not-found-page/not-found-page";

import AppSidebar from "containers/app-sidebar/app-sidebar";
import Logout from "components/partials/logout/logout";
import SmsSimulator from "containers/sms-simulator/sms-simulator";
import ReviewsRoutes from "containers/client/reviews/reviews-routes";
import ApprovedPage from "containers/client/approved/approved";

import { useCurrentUser } from "hooks/api/use-current-user";
import { FeatureFlagProvider } from "contexts/feature-flag-context";
import { UnsavedChangesNavigationProvider } from "contexts/unsaved-changes-navigation-context";

import useSetPageTitle from "hooks/use-set-page-title";
import { getTouchpointPaths, Route as AppRoute } from "utilities/app-routes";

import { Role } from "types/auth";

const AuthenticatedAppContent = () => {
  const currentUser = useCurrentUser();
  const location = useLocation();

  const isSuperAdminUser = currentUser.roles[0] === Role.ROLE_SUPER_ADMIN;
  const shouldHideSidebarForBeeEditor = /\/(email-builder|landing-page-builder)(\/|$)/.test(
    location.pathname,
  );
  const shouldShowSidebar = !shouldHideSidebarForBeeEditor;
  useSetPageTitle({ isClientUser: !isSuperAdminUser });

  FullStory("setIdentity", {
    uid: currentUser.id,
    properties: {
      email: currentUser.email,
    },
  });

  const { touchpointPath, touchpointPreviewPath } = getTouchpointPaths();

  const routes = (
    <Routes>
      {/* Root redirect */}
      {["/", "/login"].map((path) => (
        <Route
          key={path}
          path={path}
          element={<Navigate to={isSuperAdminUser ? "/clients" : "/reviews"} replace />}
        />
      ))}

      {/* Admin-only routes - protected from client users */}
      {isSuperAdminUser && (
        <>
          <Route path="/clients/*" element={<ClientsRoutes />} />
          <Route path="/admin/*" element={<AdminRoutes />} />
          {/* Legacy inbox routes are deprecated; Recent creatives is client-scoped under /clients/:clientId */}
          <Route path="/inbox/*" element={<Navigate to="/clients" replace />} />
        </>
      )}

      {/* Special case: Allow access client touchpoint and preview paths */}
      {!isSuperAdminUser && (
        <>
          <Route path={touchpointPath} element={<TouchpointPageRedirect />} />
          <Route
            path={`${touchpointPreviewPath}/*`}
            element={
              <ClientProvider>
                <CampaignProvider>
                  <CreativeProvider>
                    <TouchpointPage />
                  </CreativeProvider>
                </CampaignProvider>
              </ClientProvider>
            }
          />
        </>
      )}

      {/* Client-only routes
        @NOTE: These are *not* client-only routes. They are open to any authenticated user.
      */}
      <Route path="/reviews/*" element={<ReviewsRoutes />} />
      <Route path="/approved" element={<ApprovedPage />} />

      <Route path={`${AppRoute.smsSimulator}/*`} element={<SmsSimulator />} />

      <Route path="/logout" element={<Logout />} />

      {/* 404 Route */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );

  return (
    <UnsavedChangesNavigationProvider>
      <div className="flex h-full min-h-0">
        {shouldShowSidebar && <AppSidebar />}
        <div
          data-testid="app-content-area"
          className="relative flex min-w-0 min-h-0 flex-1 flex-col overflow-hidden">
          <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">{routes}</div>
        </div>
      </div>
    </UnsavedChangesNavigationProvider>
  );
};

const AuthenticatedApp = () => (
  <FeatureFlagProvider>
    <AuthenticatedAppContent />
  </FeatureFlagProvider>
);

export default AuthenticatedApp;
